/* eslint-disable no-console */

import type { Node as PMNode, Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { updateStreamPositions } from '../transforms';
import { convertADFToFragment, convertMarkdownToFragment } from '../utils/adfConversion';

/**
 * Checks if config is for a markdown streaming flow.
 * Markdown streaming flows apply changes outside the ProseMirror document
 * (e.g. updating a page title) and use a separate transaction builder.
 */
export function isMarkdownStreamingConfig(configItem: EditorPluginAIConfigItem): boolean {
	if (!configItem) {
		return false;
	}
	return !!configItem.streamsMarkdown;
}

/**
 * Builds transaction for markdown streaming flows (e.g. suggest title).
 *
 * For markdown streaming, the actual change is applied outside the ProseMirror document
 * (e.g. updating the Confluence page title via an eventHub event published by
 * handleMarkdownStreaming). This transaction builder attempts to update the document's
 * title/heading node as well, but even if that fails (e.g., no title/heading node exists),
 * it still dispatches updateStreamPositions so the streaming lifecycle progresses and the
 * view handler can publish the external event.
 */
export function buildMarkdownStreamingTransaction(
	state: EditorState,
	isFinalStream: boolean,
): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	if (!decorationPluginState) {
		return null;
	}
	const { configItem } = decorationPluginState;
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		return null;
	}
	const { adfResponse, markdown, idMap, responseVersion } = sessionState;
	if (!configItem || (!adfResponse && !markdown) || responseVersion == null) {
		return null;
	}

	if (!isMarkdownStreamingConfig(configItem)) {
		return null;
	}

	const fragment =
		convertADFToFragment(state.schema, adfResponse) ??
		(isExperimentEnabled('show_mentions_in_suggest_reply')
			? convertMarkdownToFragment(state.schema, markdown, idMap)
			: undefined);
	if (!fragment) {
		return null;
	}

	// Try to update the ProseMirror document's title/heading node.
	// This may return null if no title/heading node exists (e.g., in Confluence
	// where the page title is a separate DOM element outside the editor).
	const docTr = replaceTitleOrHeading(state, fragment);

	// Always dispatch updateStreamPositions so the streaming lifecycle progresses,
	// regardless of whether the ProseMirror doc was modified.
	// This ensures handleMarkdownStreaming view handler fires and publishes
	// the eventHub event to update the actual Confluence page title textarea.
	const tr = docTr ?? state.tr;

	return updateStreamPositions({
		aiContentEndPos: 0,
		telepointerPos: 0,
		isFinalStream,
		lastProcessedVersion: responseVersion,
	})(tr);
}

/**
 * Attempts to replace the title or first heading node in the ProseMirror document
 * with the streamed markdown content.
 *
 * Note: This could serve as a provisional approach for when a title node is added
 * to the ADF schema — at that point, the title could be updated directly in the
 * document rather than relying on external eventHub events.
 */
function replaceTitleOrHeading(state: EditorState, fragment: Fragment): Transaction | null {
	const { doc, schema } = state;

	let titleText = '';
	fragment.forEach((node) => {
		titleText += node.textContent;
	});

	// Look for title node type
	const titleType = schema.nodes.title;
	if (titleType) {
		let titlePos: number | null = null;
		let titleNode: PMNode | null = null;

		doc.descendants((node, pos) => {
			if (node.type === titleType && titlePos === null) {
				titlePos = pos;
				titleNode = node;
				return false;
			}
			return true;
		});

		if (titlePos !== null && titleNode) {
			const tr = state.tr;
			const titleStart = titlePos + 1;
			const titleEnd = titlePos + ((titleNode as PMNode).nodeSize - 1);

			try {
				if (titleText) {
					tr.insertText(titleText, titleStart, titleEnd);
				} else {
					tr.delete(titleStart, titleEnd);
				}
				return tr;
			} catch {
				// Title replacement failed - try fallback
			}
		}
	}

	// Fallback: first heading
	const { heading } = schema.nodes;
	if (heading) {
		let headingPos: number | null = null;
		let headingNode: PMNode | null = null;

		doc.descendants((node, pos) => {
			if (node.type === heading && headingPos === null) {
				headingPos = pos;
				headingNode = node;
				return false;
			}
			return true;
		});

		if (headingPos !== null && headingNode) {
			const tr = state.tr;
			const headingStart = headingPos + 1;
			const headingEnd = headingPos + ((headingNode as PMNode).nodeSize - 1);

			try {
				if (titleText) {
					tr.insertText(titleText, headingStart, headingEnd);
				} else {
					tr.delete(headingStart, headingEnd);
				}
				return tr;
			} catch {
				// Heading replacement failed
			}
		}
	}

	return null;
}
